import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import { Logo1Icon } from '@blocksuite/icons/rc';
import { useTheme } from 'next-themes';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import dotBgDark from './assets/dot-bg.dark.png';
import dotBgLight from './assets/dot-bg.light.png';
import {
  getInstanceBranding,
  isSafeBrandingUrl,
  serverBrandingQuery,
} from './branding';
import { DesktopNavbar } from './desktop-navbar';
import * as styles from './index.css';
import { MobileNavbar } from './mobile-navbar';

function applyFavicon(url: string) {
  const links = document.querySelectorAll<HTMLLinkElement>(
    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
  );
  links.forEach(link => {
    link.href = url;
  });
}

function useInstanceBranding() {
  const [branding, setBranding] = useState(getInstanceBranding);

  useEffect(() => {
    let active = true;
    fetch('/graphql', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        query: serverBrandingQuery,
      }),
    })
      .then(response => response.json())
      .then(result => {
        const configured = result?.data?.serverConfig;
        if (!active || !configured) return;

        const hasFaviconConfig = typeof configured.faviconUrl === 'string';
        const branding = getInstanceBranding(
          configured,
          environment.isSelfHosted
        );
        setBranding(branding);
        if (hasFaviconConfig) {
          applyFavicon(
            isSafeBrandingUrl(configured.faviconUrl)
              ? configured.faviconUrl.trim()
              : branding.faviconUrl
          );
        }
        document.title = branding.name;
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, []);

  return branding;
}

export const AffineOtherPageLayout = ({
  children,
}: {
  children: ReactNode;
}) => {
  const t = useI18n();
  const branding = useInstanceBranding();
  const [logoUrl, setLogoUrl] = useState(branding.logoUrl);

  useEffect(() => {
    setLogoUrl(branding.logoUrl);
  }, [branding.logoUrl]);

  const openDownloadLink = useCallback(() => {
    open(BUILD_CONFIG.downloadUrl, '_blank');
  }, []);

  const { resolvedTheme } = useTheme();
  const backgroundImage =
    resolvedTheme === 'dark' && dotBgDark ? dotBgDark : dotBgLight;

  return (
    <div
      className={styles.root}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {BUILD_CONFIG.isElectron ? (
        <div className={styles.draggableHeader} />
      ) : (
        <div className={styles.topNav}>
          <a href="/" rel="noreferrer" className={styles.affineLogo}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${branding.name} logo`}
                width={24}
                height={24}
                onError={() => {
                  setLogoUrl(current =>
                    current === branding.fallbackLogoUrl
                      ? undefined
                      : branding.fallbackLogoUrl
                  );
                }}
              />
            ) : (
              <Logo1Icon width={24} height={24} />
            )}
            <span className={styles.brandText}>
              <span className={styles.brandName}>{branding.name}</span>
              {branding.subtitle ? (
                <span className={styles.brandSubtitle}>
                  {branding.subtitle}
                </span>
              ) : null}
            </span>
          </a>

          <DesktopNavbar />
          <Button
            onClick={openDownloadLink}
            className={styles.hideInSmallScreen}
          >
            {t['com.affine.auth.open.affine.download-app']()}
          </Button>
          <MobileNavbar />
        </div>
      )}

      {children}
    </div>
  );
};
